import * as React from "react";
import { cva } from "class-variance-authority";
import { CircleAlert, CircleCheck, Info, TriangleAlert, X } from "./icons";

import { cn } from "utils/cn";

export type Tone = "neutral" | "info" | "success" | "warning" | "danger";

const alertVariants = cva("relative flex gap-3 rounded-card border px-4 py-3 text-sm", {
  variants: {
    tone: {
      neutral: "border-border bg-surface-muted text-foreground",
      info: "border-info/25 bg-info-subtle text-info-text",
      success: "border-success/25 bg-success-subtle text-success-text",
      warning: "border-warning/25 bg-warning-subtle text-warning-text",
      danger: "border-danger/25 bg-danger-subtle text-danger-text",
    },
  },
  defaultVariants: { tone: "info" },
});

const iconTone: Record<Tone, string> = {
  neutral: "text-muted-foreground",
  info: "text-info",
  success: "text-success",
  warning: "text-warning",
  danger: "text-danger",
};

export const toneIcons: Record<Tone, React.ComponentType<{ className?: string }>> = {
  neutral: Info,
  info: Info,
  success: CircleCheck,
  warning: TriangleAlert,
  danger: CircleAlert,
};

export interface AlertProps {
  tone?: Tone;
  title?: React.ReactNode;
  children?: React.ReactNode;
  /** Replace the tone's default icon; pass `null` for none. */
  icon?: React.ReactNode | null;
  /** Buttons or links shown under the text. */
  action?: React.ReactNode;
  onDismiss?: () => void;
  className?: string;
}

export const Alert: React.FC<AlertProps> = ({
  tone = "info",
  title,
  children,
  icon,
  action,
  onDismiss,
  className,
}) => {
  const ToneIcon = toneIcons[tone];
  return (
    <div
      data-ui="alert"
      role={tone === "danger" || tone === "warning" ? "alert" : "status"}
      className={cn(alertVariants({ tone }), className)}
    >
      {icon !== null && (
        <span className={cn("mt-0.5 shrink-0 [&_svg]:h-4 [&_svg]:w-4", iconTone[tone])}>
          {icon ?? <ToneIcon />}
        </span>
      )}
      <div className="min-w-0 flex-1 space-y-1">
        {title && <p className="font-medium leading-5">{title}</p>}
        {children && <div className={cn("leading-5", title && "opacity-90")}>{children}</div>}
        {action && <div className="flex flex-wrap gap-2 pt-1.5">{action}</div>}
      </div>
      {onDismiss && (
        <button
          type="button"
          onClick={onDismiss}
          aria-label="Dismiss"
          className="-mr-1 -mt-0.5 h-6 w-6 shrink-0 rounded-md opacity-70 transition-opacity hover:opacity-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring [&_svg]:mx-auto [&_svg]:h-4 [&_svg]:w-4"
        >
          <X aria-hidden="true" />
        </button>
      )}
    </div>
  );
};
